import React, { useEffect, useState, useMemo } from 'react';
import questionQueries from '../../queries/questionQueries';
import { useSelector } from 'react-redux';
import { decode } from 'html-entities';
import Loader from '../../components/Loader';
import { Checkbox } from '@nextui-org/react';
import toast from 'react-hot-toast';

const QuestionExclude = () => {

  const inspection = useSelector((state) => state.master.inspection);
  const [questions, setQuestions] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('chapters'); // 'chapters' or 'all'

  const getAllQuestions = questionQueries.useGetVesselWiseQuestions((data) => {
    setQuestions(data?.questions);
    setChapters(data?.chapters);
    setLoading(false);
  });

  const excludeQuestion = questionQueries.useExcludeQuestion((res) => {
    if(res?.isLimitOver) {
      toast.error(res?.message)
    }
    getAllQuestions.mutateAsync({ inspection_id: inspection });
  });

  useEffect(() => {
    getAllQuestions.mutateAsync({ inspection_id: inspection });
  }, []);

  const filteredQuestions = useMemo(() => {
    return questions.filter((question) => {
      if (selectedChapter && question?.chapter_id !== Number(selectedChapter)) {
        return false;
      }
      
      if (filterStatus === 'included' && question?.isExcluded) return false;
      if (filterStatus === 'excluded' && !question?.isExcluded) return false;
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const questionText = decode(question?.question).toLowerCase();
        const chapterName = question?.chapter?.name?.toLowerCase() || '';
        const tag = question?.tag?.toLowerCase() || '';
        
        return (
          questionText.includes(searchLower) ||
          chapterName.includes(searchLower) ||
          tag.includes(searchLower)
        );
      }
      
      return true;
    });
  }, [questions, selectedChapter, searchTerm, filterStatus]);

  // Group questions by chapter
  const groupedQuestions = useMemo(() => {
    const grouped = {};
    
    filteredQuestions.forEach((question) => {
      const chapterId = question.chapter_id;
      const chapterName = question.chapter?.name;
      
      if (!grouped[chapterId]) {
        grouped[chapterId] = {
          id: chapterId,
          name: chapterName,
          chapter_no: question.chapter?.chapter_no,
          questions: [],
          totalIncluded: 0,
          totalExcluded: 0,
        };
      }
      
      grouped[chapterId].questions.push(question);
      
      if (question.isExcluded) {
        grouped[chapterId].totalExcluded++;
      } else {
        grouped[chapterId].totalIncluded++;
      }
    });
    
    return Object.values(grouped).sort((a, b) => a.chapter_no - b.chapter_no);
  }, [filteredQuestions]);

  const handleSelectChapter = (e) => {
    setSelectedChapter(e.target.value);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
  };

  const handleExcludeItem = (id, type) => {
    excludeQuestion.mutateAsync({ inspection_id: inspection, question_id: [id], type });
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions((prev) => {
      if (prev.includes(questionId)) {
        return prev.filter((id) => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleBatchExclude = (type) => {
    if (selectedQuestions.length === 0) return;
    excludeQuestion.mutateAsync({ inspection_id: inspection, question_id: selectedQuestions, type });
    setSelectedQuestions([]);
  };

  const selectAllChapterQuestions = (chapterId) => {
    const chapterQuestions = questions.filter((q) => q.chapter_id === chapterId);
    const chapterQuestionIds = chapterQuestions.map((q) => q.id);
    
    setSelectedQuestions((prev) => {
      const currentSelected = new Set(prev);
      const allSelected = chapterQuestions.every(q => currentSelected.has(q.id));
      
      if (allSelected) {
        return prev.filter(id => !chapterQuestionIds.includes(id));
      } else {
        const newSelections = [...prev];
        chapterQuestionIds.forEach(id => {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        });
        return newSelections;
      }
    });
  };
  
  const areAllChapterQuestionsSelected = (chapterId) => {
    const chapterQuestions = questions.filter(q => q.chapter_id === chapterId);
    return chapterQuestions.every(q => selectedQuestions.includes(q.id));
  };
  
  const areSomeChapterQuestionsSelected = (chapterId) => {
    const chapterQuestions = questions.filter(q => q.chapter_id === chapterId);
    return chapterQuestions.some(q => selectedQuestions.includes(q.id)) && 
           !chapterQuestions.every(q => selectedQuestions.includes(q.id));
  };

  const QuestionCard = ({ question, isSelected, onSelect }) => {
    return (
      <div
      className={`w-full relative transition-all duration-300 rounded-xl overflow-hidden hover:translate-y-[-2px]
      ${isSelected ? 'border-[1px] border-green-500' : ''}`}
      >
        {
          !isSelected &&
          <div className={`absolute top-0 left-0 w-1.5 h-full ${question.isExcluded ? 'bg-red-500' : 'bg-emerald-500'}`}></div>
        }
        
        <div className="h-full px-5 py-4 bg-white shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-start">
            <div className="mr-3 mt-1">
              <Checkbox
              isSelected={isSelected}
              onChange={() => onSelect(question.id)}
              color='success'
              classNames={{
                icon: 'text-white'
              }}
              />
            </div>
            
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="px-2.5 py-1 bg-primaryGradient text-white text-xs font-medium rounded-md">
                  {question?.chapter?.chapter_no}.{question?.section}.{question?.sub_section}
                </div>
                
                {question.isExcluded && (
                  <div className="px-2.5 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-md flex items-center">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></span>
                    Excluded
                  </div>
                )}
                
                {question?.tag && (
                  <div className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                    {question?.tag}
                  </div>
                )}
                
                <button
                  onClick={() => handleExcludeItem(question?.id, question?.isExcluded ? 'include' : 'exclude')}
                  className={`ml-auto text-xs font-medium rounded-full px-3 py-1 transition-colors
                            ${question?.isExcluded 
                              ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' 
                              : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                >
                  {question.isExcluded ? 'Include' : 'Exclude'}
                </button>
              </div>
              
              <p className="text-gray-700 text-sm leading-relaxed">
                {decode(question.question)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ChapterSection = ({ chapter }) => {
    const allSelected = areAllChapterQuestionsSelected(chapter.id);
    const someSelected = areSomeChapterQuestionsSelected(chapter.id);
    const totalQuestions = chapter.questions.length;
    
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Checkbox
            isSelected={allSelected}
            color={allSelected ? 'success' : 'secondary'}
            classNames={{
              icon: 'text-white'
            }}
            isIndeterminate={someSelected && !allSelected}
            onChange={() => selectAllChapterQuestions(chapter.id)}
            className="w-5 h-5 rounded-md border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
            />
            
            <div className="flex items-center ml-4">
              <div className="flex items-center justify-center w-10 h-10 bg-primaryGradient text-white font-bold rounded-lg mr-3">
                {chapter.chapter_no}
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{chapter.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-gray-500">{totalQuestions} questions</span>
                  
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-1.5"></span>
                    <span className="text-xs text-gray-600">{chapter.totalIncluded} included</span>
                  </div>
                  
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></span>
                    <span className="text-xs text-gray-600">{chapter.totalExcluded} excluded</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {chapter.questions.map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              isSelected={selectedQuestions.includes(question.id)}
              onSelect={handleSelectQuestion}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pb-10">
    
        <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search questions, tags, or chapters..."
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring focus:ring-blue-200 focus:border-blue-400 transition-all duration-200"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <select
                  className="w-full py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring focus:ring-blue-200 focus:border-blue-400 transition-all duration-200 appearance-none"
                  value={selectedChapter}
                  onChange={handleSelectChapter}
                  style={{ 
                    backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,<svg width=\"20\" height=\"20\" viewBox=\"0 0 20 20\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z\" fill=\"%236B7280\"/></svg>')",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "1.25rem"
                  }}
                >
                  <option value="">All Chapters</option>
                  {chapters?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="px-5 py-3 bg-gray-50 flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg overflow-hidden shadow-sm mr-4">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-4 py-2 text-sm font-medium ${
                  filterStatus === 'all'
                    ? 'bg-primaryGradient text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                } transition-colors border-r border-gray-200`}
              >
                All Questions ({questions.length})
              </button>
              <button
                onClick={() => handleStatusFilter('included')}
                className={`px-4 py-2 text-sm font-medium ${
                  filterStatus === 'included'
                    ? 'bg-successGradient text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                } transition-colors border-r border-gray-200`}
              >
                Included ({questions.filter(q => !q.isExcluded).length})
              </button>
              <button
                onClick={() => handleStatusFilter('excluded')}
                className={`px-4 py-2 text-sm font-medium ${
                  filterStatus === 'excluded'
                    ? 'bg-errorGradient text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                } transition-colors`}
              >
                Excluded ({questions.filter(q => q.isExcluded).length})
              </button>
            </div>
            
            <div className="flex ml-auto">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'chapters'
                    ? 'bg-primaryGradient text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                } transition-colors rounded-l-lg border border-gray-200`}
              >
                Chapter View
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'all'
                    ? 'bg-primaryGradient text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                } transition-colors rounded-r-lg border-t border-r border-b border-gray-200`}
              >
                All Questions
              </button>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : filteredQuestions.length > 0 ? (
          activeTab === 'chapters' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-end gap-2">
                <div className="flex">
                  <button
                    onClick={() => handleBatchExclude('exclude')}
                    className="py-1.5 px-3 text-xs font-medium text-red-700 bg-red-100 rounded-l-md hover:bg-red-100 transition-colors border-r border-red-200"
                  >
                    Exclude All
                  </button>
                  <button
                    onClick={() => handleBatchExclude('include')}
                    className="py-1.5 px-3 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-r-md hover:bg-emerald-100 transition-colors"
                  >
                    Include All
                  </button>
                </div>
              </div>
              {groupedQuestions.map((chapter) => (
                <div key={chapter.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="border-b border-gray-100 bg-gradient-to-r from-gray-100 to-white px-6 py-4">
                    <ChapterSection chapter={chapter} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredQuestions.map((question) => (
                    <QuestionCard
                      key={question.id}
                      question={question}
                      isSelected={selectedQuestions.includes(question.id)}
                      onSelect={handleSelectQuestion}
                    />
                  ))}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="bg-white rounded-xl shadow-md p-16 text-center">
            <div className="w-20 h-20 mx-auto bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Questions Found</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {searchTerm || selectedChapter || filterStatus !== 'all'
                ? 'Try adjusting your filters or search terms to find what you'
                : 'There are no questions available for this inspection at the moment.'}
            </p>
            {(searchTerm || selectedChapter || filterStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedChapter('');
                  setFilterStatus('all');
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Clear Filters
              </button>
            )}
          </div>
        )}
    </div>
  );
};

export default QuestionExclude;